import React from "react";
import { Route, Navigate } from "react-router-dom";

import { paths } from "../../routes/paths";
import PrivateRoute from "../../routes/PrivateRoute";
import RoleRoute from "../../routes/RoleRoute";
import { ROLES } from "../hooks/useRole";

import V1Theme from "../V1Theme";
import ConsoleLayout from "../layouts/ConsoleLayout";
import Console from "../pages/Console";
import ConsoleIndex from "../pages/Console/ConsoleIndex";
import ResumePoolSearch from "../pages/ResumePool";
import CandidateProfile from "../pages/Candidate";
import CompanyUsers from "../pages/Admin/CompanyUsers";
import Companies from "../pages/Admin/Companies";
import AllJobs from "../pages/Admin/AllJobs";
import AllUsers from "../pages/Admin/AllUsers";
import SignIn from "../pages/auth/Login";
import SignUp from "../pages/auth/SignUp";
import ForgotPwd from "../pages/auth/ForgotPwd";
import ResetPwd from "../pages/auth/ResetPwd";

/**
 * Routes for the Console revamp, all under /v1.
 *
 * Exported as a fragment of <Route> elements rather than its own <Routes>, so it drops
 * straight into the app's single router. The original UI's routes are untouched — the
 * two run side by side, sharing services, store and auth token, until /v1 hits parity.
 *
 * Everything sits under V1Theme, which is the only place the console's MUI theme is
 * applied. That boundary is what keeps IBM Plex/indigo out of the old pages.
 */
const v1Routes = (
  <Route element={<V1Theme />}>
    <Route path={paths.v1.signin} element={<SignIn />} />
    <Route path={paths.v1.signup} element={<SignUp />} />
    <Route path={paths.v1.forgotPassword} element={<ForgotPwd />} />
    <Route path={paths.v1.resetPassword} element={<ResetPwd />} />

    <Route
      path={paths.v1.jobs}
      element={
        <PrivateRoute>
          <ConsoleLayout />
        </PrivateRoute>
      }
    >
      <Route index element={<ConsoleIndex />} />
      <Route path=":jobId" element={<Console />} />
    </Route>

    <Route
      path={paths.v1.resumePool}
      element={
        <PrivateRoute>
          <ConsoleLayout />
        </PrivateRoute>
      }
    >
      <Route index element={<ResumePoolSearch />} />
    </Route>

    {/* Admin. Inside the console shell so the rail stays put — these used to live in
        the old UI, which meant clicking them ejected you out of the console.

        RoleRoute here is UX only — it hides a page from someone who shouldn't see the
        nav link, it does not protect the data underneath. The server's @require_role
        is the actual enforcement; these routes just stop a signed-in member from
        reaching a page via a typed URL. */}
    <Route
      path={paths.v1.companyUsers}
      element={
        <RoleRoute allow={[ROLES.COMPANY_ADMIN, ROLES.SUPER_ADMIN]}>
          <ConsoleLayout />
        </RoleRoute>
      }
    >
      <Route index element={<CompanyUsers />} />
    </Route>

    <Route
      path={paths.v1.companies}
      element={
        <RoleRoute allow={[ROLES.SUPER_ADMIN]}>
          <ConsoleLayout />
        </RoleRoute>
      }
    >
      <Route index element={<Companies />} />
    </Route>

    <Route
      path={paths.v1.allJobs}
      element={
        <RoleRoute allow={[ROLES.SUPER_ADMIN]}>
          <ConsoleLayout />
        </RoleRoute>
      }
    >
      <Route index element={<AllJobs />} />
    </Route>

    <Route
      path={paths.v1.allUsers}
      element={
        <RoleRoute allow={[ROLES.SUPER_ADMIN]}>
          <ConsoleLayout />
        </RoleRoute>
      }
    >
      <Route index element={<AllUsers />} />
    </Route>

    {/* Full resume. Opened in a new tab from the console, so it has no rail/top bar —
        it's a document, not a workspace. */}
    <Route path={paths.v1.candidate} element={<CandidateProfile />} />

    <Route path={paths.v1.root} element={<Navigate to={paths.v1.jobs} replace />} />
  </Route>
);

export default v1Routes;
